import { supabase } from '@/lib/supabase';
import { ROOM_JOIN, stripRoomJoin } from '@/lib/data/room-scoped';
import type { Database, SpecRow } from '@/types/database';

type SpecInsert = Database['public']['Tables']['specs']['Insert'];
type SpecUpdate = Database['public']['Tables']['specs']['Update'];

export async function listSpecs(roomId: string): Promise<SpecRow[]> {
  const { data, error } = await supabase
    .from('specs')
    .select('*')
    .eq('room_id', roomId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function listSpecsForProject(projectId: string): Promise<SpecRow[]> {
  // ONE request, through the room it hangs off. This was two — rooms, then
  // specs — which cost a whole extra round trip AND could not start until the
  // first came back. Four modules did the same thing, so opening a project
  // asked for the same room list four times (2026-09-22). RLS still checks
  // both tables; the embed only changes how many trips it takes.
  const { data, error } = await supabase
    .from('specs')
    .select(`*, ${ROOM_JOIN}`)
    .eq('rooms.project_id', projectId);
  if (error) throw error;
  return stripRoomJoin<SpecRow>(data);
}

export async function createSpec(input: SpecInsert): Promise<SpecRow> {
  const { data, error } = await supabase.from('specs').insert(input).select().single();
  if (error) throw error;
  return data;
}

export async function updateSpec(id: string, patch: SpecUpdate): Promise<SpecRow> {
  const { data, error } = await supabase.from('specs').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteSpec(id: string): Promise<void> {
  const { error } = await supabase.from('specs').delete().eq('id', id);
  if (error) throw error;
}
